import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import AnalyticsApiService, { AiFriendUsage } from '../../services/analyticsApi';

interface AiFriendUsageChartProps {
  className?: string;
}

const COLORS = [
  '#3B82F6', // blue-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#6366F1', // indigo-500
  '#14B8A6', // teal-500
  '#F97316', // orange-500
];

export function AiFriendUsageChart({ className }: AiFriendUsageChartProps) {
  const [data, setData] = useState<AiFriendUsage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Call endpoint without timestamps - defaults to last 30 days
      const usage = await AnalyticsApiService.getAiFriendUsage();
      
      setData(usage);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch AI friend usage:', error);
      setError('Failed to load AI friend usage data');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 300000); // 5 minutes = 300,000ms
    return () => clearInterval(interval);
  }, []);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-purple-200 dark:border-purple-700 rounded-xl p-4 shadow-xl">
          <p className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100 text-sm mb-2">
            {item.default_name || `AI Friend #${item.ai_friend_id}`}
          </p>
          <p className="font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-purple-600 dark:text-purple-400 text-lg">
            {payload[0].value.toLocaleString()} messages
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            ID: {item.ai_friend_id}
          </p>
        </div>
      );
    }
    return null;
  };

  const totalMessages = data.reduce((sum, d) => sum + d.total_messages, 0);
  const avgMessages = data.length > 0 ? Math.round(totalMessages / data.length) : 0;
  const topAiFriend = data.length > 0 ? data[0] : null;

  // Prepare chart data with display names
  const chartData = data.map((item, index) => ({
    ...item,
    name: item.default_name || `AI #${item.ai_friend_id}`,
    displayName: item.default_name?.substring(0, 12) || `AI #${item.ai_friend_id}`,
    color: COLORS[index % COLORS.length],
  }));

  return (
    <Card className={`bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100">
                  AI Friend Usage
                </h3>
                <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400">
                  Message volume by AI friend
                </p>
              </div>
            </div>
            
            {/* Summary Stats */}
            {!isLoading && !error && (
              <div className="flex items-center gap-6 mt-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Messages</p>
                  <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-purple-600 dark:text-purple-400">
                    {totalMessages.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Active AI Friends</p>
                  <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-pink-600 dark:text-pink-400">
                    {data.length}
                  </p>
                </div>
                {topAiFriend && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Most Active</p>
                    <p className="text-lg font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-purple-700 dark:text-purple-300">
                      {topAiFriend.default_name || `AI #${topAiFriend.ai_friend_id}`}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {topAiFriend.total_messages.toLocaleString()} msgs
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Last Updated & Refresh Button */}
          <div className="flex items-center gap-3 ml-4">
            {lastUpdated && !isLoading && (
              <p className="text-xs text-gray-500 dark:text-gray-400 font-['SF_Pro_Text',_-apple-system,_sans-serif]">
                Updated {lastUpdated.toLocaleTimeString()}
              </p>
            )}
            <button
              onClick={() => fetchData()}
              disabled={isLoading}
              className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <svg 
                className={`w-5 h-5 text-gray-700 ${isLoading ? 'animate-spin' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6" style={{ height: 400 }}>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-600 dark:text-red-400 font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold">{error}</p>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-400 font-['SF_Pro_Text',_-apple-system,_sans-serif]">No AI friend activity in this period</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartData} 
              layout="vertical"
              margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
              <XAxis 
                type="number"
                tick={{ 
                  fill: '#6B7280', 
                  fontFamily: 'SF Pro Text, -apple-system, sans-serif',
                  fontSize: 12 
                }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <YAxis 
                type="category"
                dataKey="displayName" 
                tick={{ 
                  fill: '#374151', 
                  fontFamily: 'SF Pro Text, -apple-system, sans-serif',
                  fontSize: 13,
                  fontWeight: 500
                }}
                width={110}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F3F4F6' }} />
              <Bar 
                dataKey="total_messages" 
                radius={[0, 8, 8, 0]}
                animationDuration={1000}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Info Footer */}
      <div className="px-6 pb-6">
        <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-700 dark:text-gray-300">
                <span className="font-semibold text-purple-600 dark:text-purple-400">AI Friend Usage</span> shows message volume by each AI friend assistant over the <span className="font-semibold">last 30 days</span>. 
                Rankings include both active and inactive AI friends to show historical usage patterns.
              </p>
              {data.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  📊 Showing {data.length} AI friend{data.length !== 1 ? 's' : ''} • Updates every 5 minutes
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

